/**
 * Danger zone — data-subject toolkit.
 *
 * Self-serve actions:
 *   - "Download my data" → /api/account/export (JSON, all collections tied to the user)
 *   - "Delete my account" → /api/account/me (cascading delete with confirmation)
 *
 * For rectification / restriction-of-processing requests, we direct the user to
 * marcelle@smartnote2self.com — a documented 30-day SLA fulfils GDPR Art. 16–18
 * without needing a dedicated UI for the long tail.
 */
import { useState } from "react";
import { AlertTriangle, Download, Mail, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function DangerZone({ onDelete, deleting }) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [exporting, setExporting] = useState(false);

  const downloadMyData = async () => {
    try {
      setExporting(true);
      const res = await api.get("/account/export", { responseType: "blob" });
      // Try to parse the filename out of Content-Disposition; fall back to a
      // sensible default.
      const cd = res.headers["content-disposition"] || "";
      const match = cd.match(/filename="?([^";]+)"?/i);
      const filename = match ? match[1] : `smartnote2self-export-${Date.now()}.json`;
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("Your data export has downloaded.");
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "Could not generate export.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="mt-10 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6">
      <div className="flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
        <p className="text-[11px] uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">Data &amp; privacy</p>
      </div>
      <h3 className="mt-2 font-display text-lg font-semibold text-slate-900 dark:text-slate-50">Your data, your control</h3>
      <p className="mt-1.5 text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
        Three actions you can take on your own data — instantly, no email or wait required.
      </p>

      {/* Self-serve actions ------------------------------------------- */}
      <div className="mt-5 grid gap-3 sm:grid-cols-2">
        <button
          onClick={downloadMyData}
          disabled={exporting}
          data-testid="export-data-btn"
          className="inline-flex items-center justify-center gap-2 h-11 px-4 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-50 text-sm font-medium hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors disabled:opacity-50"
        >
          {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          {exporting ? "Preparing…" : "Download my data (JSON)"}
        </button>
        <a
          href="mailto:marcelle@smartnote2self.com?subject=Data%20correction%20or%20restriction%20request"
          data-testid="email-privacy-btn"
          className="inline-flex items-center justify-center gap-2 h-11 px-4 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-50 text-sm font-medium hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
        >
          <Mail className="h-4 w-4" />
          Email marcelle@smartnote2self.com
        </a>
      </div>
      <p className="mt-3 text-[11px] text-slate-500 dark:text-slate-400 leading-relaxed">
        Use the export for portability or a record. For corrections or restriction-of-processing requests email us — we respond within 30 days.
      </p>

      {/* Destructive: delete account --------------------------------- */}
      <div className="mt-6 border-t border-slate-200 dark:border-slate-800 pt-5">
        <h4 className="font-display text-base font-semibold text-slate-900 dark:text-slate-50">Delete my account and all data</h4>
        <p className="mt-1.5 text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
          Permanently removes your profile, all assessments, share links, delivery logs, payments, and bookings.
          This action cannot be undone.
        </p>
        {!confirmOpen ? (
          <button
            onClick={() => setConfirmOpen(true)}
            data-testid="delete-account-btn"
            className="mt-4 inline-flex items-center gap-2 h-10 px-4 rounded-md border border-red-300 dark:border-red-500/40 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-50 text-sm font-medium hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
          >
            <Trash2 className="h-4 w-4" /> Delete my account
          </button>
        ) : (
          <div className="mt-4 rounded-lg border border-red-200 dark:border-red-500/30 bg-red-50/60 dark:bg-red-500/10 p-4">
            <p className="text-sm text-slate-900 dark:text-slate-100">
              Type <span className="font-mono font-semibold">DELETE</span> to confirm.
            </p>
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                className="h-10 px-3 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-red-500/30"
                data-testid="delete-confirm-input"
              />
              <button
                onClick={() => onDelete(confirmText)}
                disabled={deleting || confirmText !== "DELETE"}
                data-testid="delete-confirm-btn"
                className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-slate-900 dark:bg-red-600 text-white text-sm font-medium hover:bg-slate-800 dark:hover:bg-red-500 transition-colors disabled:opacity-50"
              >
                <Trash2 className="h-4 w-4" /> {deleting ? "Deleting…" : "Permanently delete"}
              </button>
              <button
                onClick={() => { setConfirmOpen(false); setConfirmText(""); }}
                className="h-10 px-4 rounded-md border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 text-sm hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
